import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Store,
  Bot,
  Ban,
  Send,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import i18n from "@/i18n";
import type { BadgeVariant } from "@/components/ui/badge-variants";
import { brandFillClass } from "@/components/ui/fill-variants";
import type {
  HandshakeSessionStatus,
  HandshakeMessageStatus,
  HandshakeSenderType,
} from "@/types";
import {
  formatRelativeDate as formatRelativeDateShared,
  formatFullDate as formatFullDateShared,
} from "@/components/tickets/config";

// ============ Session Status Config ============

export interface SessionStatusConfig {
  labelKey: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  iconColor: "primary" | "accent" | "success" | "warning" | "error" | "default";
  badgeVariant: BadgeVariant;
}

export const SESSION_STATUS_CONFIG: Record<
  HandshakeSessionStatus,
  SessionStatusConfig
> = {
  PENDING: {
    labelKey: "sessionStatus.PENDING",
    icon: Clock,
    color: "text-accent-600",
    bgColor: "bg-accent-50",
    iconColor: "accent",
    badgeVariant: "accent",
  },
  CONFIRMED: {
    labelKey: "sessionStatus.CONFIRMED",
    icon: CheckCircle,
    color: "text-success-600",
    bgColor: "bg-success-50",
    iconColor: "success",
    badgeVariant: "success",
  },
  EXPIRED: {
    labelKey: "sessionStatus.EXPIRED",
    icon: AlertCircle,
    color: "text-warning-600",
    bgColor: "bg-warning-50",
    iconColor: "warning",
    badgeVariant: "warning",
  },
  CANCELLED: {
    labelKey: "sessionStatus.CANCELLED",
    icon: XCircle,
    color: "text-error-600",
    bgColor: "bg-error-50",
    iconColor: "error",
    badgeVariant: "error",
  },
};

// ============ Message Status Config ============

export interface MessageStatusConfig {
  labelKey: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  badgeVariant: BadgeVariant;
}

export const MESSAGE_STATUS_CONFIG: Record<
  HandshakeMessageStatus,
  MessageStatusConfig
> = {
  DELIVERED: {
    labelKey: "messageStatus.DELIVERED",
    icon: Send,
    color: "text-success-600",
    bgColor: "bg-success-50",
    badgeVariant: "success",
  },
  BLOCKED: {
    labelKey: "messageStatus.BLOCKED",
    icon: Ban,
    color: "text-error-600",
    bgColor: "bg-error-50",
    badgeVariant: "error",
  },
};

// ============ Sender Type Config ============

export interface SenderTypeConfig {
  labelKey: string;
  icon: LucideIcon;
  color: string;
  /** Avatar tile background + icon color (contrast-safe in both themes). */
  tileClass: string;
  dotColor: string;
}

export const SENDER_TYPE_CONFIG: Record<HandshakeSenderType, SenderTypeConfig> =
  {
    CUSTOMER: {
      labelKey: "sender.CUSTOMER",
      icon: User,
      color: "text-accent-700",
      tileClass: brandFillClass("accent"),
      dotColor: "bg-accent-400",
    },
    SELLER: {
      labelKey: "sender.SELLER",
      icon: Store,
      color: "text-primary-700",
      tileClass: brandFillClass("primary"),
      dotColor: "bg-primary-400",
    },
    SYSTEM: {
      labelKey: "sender.SYSTEM",
      icon: Bot,
      color: "text-surface-600",
      tileClass: "bg-surface-200 text-surface-700",
      dotColor: "bg-surface-400",
    },
  };

const tHandshake = (key: string) => i18n.t(key, { ns: "handshake" });

export const ADMIN_MESSAGE_SENDER_CONFIG: SenderTypeConfig = {
  labelKey: "sender.ADMIN",
  icon: ShieldCheck,
  color: "text-warning-700",
  tileClass: brandFillClass("warning"),
  dotColor: "bg-warning-400",
};

export function isHandshakeAdminSentMessage(message: {
  sender_type: HandshakeSenderType;
  sent_by_admin?: boolean;
}): boolean {
  return message.sender_type === "SELLER" && message.sent_by_admin === true;
}

export function handshakeMessageSenderConfig(message: {
  sender_type: HandshakeSenderType;
  sent_by_admin?: boolean;
}): SenderTypeConfig {
  if (isHandshakeAdminSentMessage(message)) {
    return ADMIN_MESSAGE_SENDER_CONFIG;
  }
  return SENDER_TYPE_CONFIG[message.sender_type];
}

export function handshakeMessageSenderLabel(message: {
  sender_type: HandshakeSenderType;
  sent_by_admin?: boolean;
}): string {
  return tHandshake(handshakeMessageSenderConfig(message).labelKey);
}

export function handshakeSessionStatusLabel(
  status: HandshakeSessionStatus,
): string {
  return tHandshake(SESSION_STATUS_CONFIG[status].labelKey);
}

export function handshakeMessageStatusLabel(
  status: HandshakeMessageStatus,
): string {
  return tHandshake(MESSAGE_STATUS_CONFIG[status].labelKey);
}

export const formatRelativeDate = formatRelativeDateShared;
export const formatFullDate = formatFullDateShared;

export { formatDateTime } from "@/lib/datetime";

export function channelLabel(channel: string): string {
  const known = ["WHATSAPP", "SMS", "EMAIL"] as const;
  if ((known as readonly string[]).includes(channel)) {
    return tHandshake(`channel.${channel}`);
  }
  return channel;
}
